import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Bell, Check, MessageCircle, ThumbsUp, UserPlus, ShieldCheck, MessageSquare, Sparkles, HelpCircle } from "lucide-react";

const iconFor = (k) => ({
  dm: MessageCircle, vote: ThumbsUp, friend_request: UserPlus,
  verified: ShieldCheck, reply: MessageSquare, update: Sparkles,
  response: HelpCircle,
}[k] || Bell);

export default function NotificationsBell() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const timer = useRef(null);

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    if (!user) return;
    load();
    timer.current = setInterval(load, 12000);
    return () => clearInterval(timer.current);
  }, [user]);

  const markAll = async () => {
    await api.post("/notifications/mark-read", { ids: null }).catch(() => {});
    load();
  };

  const openItem = async (n) => {
    if (!n.read) await api.post("/notifications/mark-read", { ids: [n.id] }).catch(() => {});
    setOpen(false);
    if (n.link) nav(n.link);
    load();
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button data-testid="nav-notifications" className="relative flex h-9 w-9 items-center justify-center border border-border hover:border-primary/60 hover:text-primary">
          <Bell className="h-4 w-4" />
          {data.unread > 0 && (
            <span data-testid="notif-badge" className="absolute -top-1 -right-1 mono bg-primary text-primary-foreground text-[9px] leading-none px-1 py-0.5">{data.unread > 9 ? "9+" : data.unread}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 rounded-none p-0">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <DropdownMenuLabel className="text-xs uppercase tracking-widest text-muted-foreground p-0">Notifications</DropdownMenuLabel>
          {data.unread > 0 && <button data-testid="notif-mark-all" onClick={markAll} className="text-[10px] uppercase tracking-widest text-primary hover:underline flex items-center gap-1"><Check className="h-3 w-3" />Mark all</button>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">No notifications yet — you'll get pings for DMs, votes, verified success, and more.</div>
          ) : data.items.map((n) => {
            const Icon = iconFor(n.kind);
            return (
              <button key={n.id} data-testid={`notif-${n.id}`} onClick={() => openItem(n)} className={`w-full text-left p-3 border-b border-border last:border-b-0 hover:bg-primary/5 flex items-start gap-3 ${!n.read ? "bg-primary/5" : ""}`}>
                <div className={`mt-0.5 h-6 w-6 flex items-center justify-center border ${!n.read ? "border-primary text-primary" : "border-border text-muted-foreground"}`}><Icon className="h-3 w-3" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm leading-snug line-clamp-3">{n.text}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">{new Date(n.created_at).toLocaleString()}</div>
                </div>
                {!n.read && <span className="mt-1 h-2 w-2 bg-primary shrink-0" />}
              </button>
            );
          })}
        </div>
        <DropdownMenuSeparator />
        <button onClick={() => { setOpen(false); nav("/settings"); }} data-testid="notif-open-settings" className="w-full p-2 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary text-center">Manage notifications →</button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
